/* QuadrantComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./QuadrantRenderer");
const _ = require("lodash");

class QuadrantComponent extends EventEmitter {
    constructor(configObj) {
        super();
        this._name = configObj.name;
        this._state = configObj.state;
        this._renderer = new Renderer(this);
        this._logger = this.DI.getRegistered("$teve.logger").registeredObj;
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */

    /*******************************************/
    _registerListeners() {
        this._state.on("stateChanged", (this._onStateChanged = this._onStateChanged.bind(this)));
    }

    _unregisterListeners() {
        this._state.removeListener("stateChanged", this._onStateChanged);
    }

    _onStateChanged() {
        if (!this._state.disabled) {
            this.renderer.render();
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */

    /*******************************************/
    get log() {
        return this._logger;
    }

    quadrantSizeChanged() {
        this._state.quadrantSizeChanged();
    }

    get name() {
        return this._name;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        this.emit("Shutdown");
        return this.renderer._shutdown().then(() => {
            this._state = null;
            this._renderer = null;
        });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */

    /*******************************************/

    requestPartActivation(partInfo, options) {
        if (this._state.getActivePart(partInfo)) {
            // console.log("there is already an instance of "+dockletType+" in the "+this.name+"quadrant")
        } else {
            // console.log("quadrant requesting a new instance of "+dockletType);
            this.emit("activatePart", this.name, partInfo, options);
        }
    }

    getActiveLMRBPartInfo(id) {
        let partInfo = this._state.getActivePart(id);
        if (partInfo) {
            return partInfo;
        } else {
            throw new Error("There is no active part with the ID " + id);
        }
    }

    get DI() {
        return this._state.DI;
    }

    set isTransitioning(val) {
        this._state.transitioning = val;
        //this._stateChanged();
    }

    get isTransitioning() {
        return this._state.transitioning;
    }

    get openedSize() {
        return this._state.openedSize;
    }

    set openedSize(val) {
        this._state.openedSize = val;
    }

    setOpenedSize(val, silent) {
        this._state.setOpenedSize(val, silent);
    }

    get openedPartId() {
        return this._state.openedPartId;
    }

    set openedPartId(val) {
        this._state.openedPartId = val;
    }

    setFullScreen(val, silent) {
        this._state.setFullScreen(val, silent);
    }

    get fullScreen() {
        return this._state.fullScreen;
    }

    set fullScreen(val) {
        this._state.fullScreen = val;
    }

    /**
     * calls open on the part with the provided ID {@link Quadrant.openedQuadrantPart}
     * @param {String} partId - The ID of the QuadrantPart that should be opened with the Quadrant
     * @returns {Promise} Resolved if both the Renderer and the QuadrantPartList are successfully
     * torn down
     */
    openLMRBPart(partId) {
        return new Promise((resolve, reject) => {
            // Must call with a partId
            if (_.isString(partId)) {
                // Check that partId corresponds to an active docklet
                let partInfo = this.getActiveLMRBPartInfo(partId);
                if (partInfo) {
                    try {
                        let part = this.DI.createInstance(partInfo.name);
                        if (partInfo.maxByDefault) {
                            this.setFullScreen(true, true);
                        }
                        resolve(part);
                    } catch (e) {
                        reject({
                            message: "registry failed to createInstance of part",
                            partInfo: partInfo,
                            partError: e,
                        });
                    }
                } else {
                    reject({
                        message:
                            "QuadrantPart with Name " +
                            partId +
                            " not registered in " +
                            this.name +
                            " Quadrant",
                    });
                }
            } else {
                throw new Error(
                    "Quadrants openQuadrant method must be called with a QuadrantPart ID as the parameter"
                );
            }
        });
    }

    /**
     * calls shutdown on the part with the provided ID
     * @param {Object} part
     * @returns {Promise}
     */
    closeLMRBPart(part) {
        return new Promise((resolve, reject) => {
            // console.log("calling shutdown on part "+part.id);
            if (part) {
                let closeProm = part.shutdown();
                if (closeProm && closeProm.toString() === "[object Promise]") {
                    closeProm.then(resolve, reject);
                } else {
                    resolve(closeProm);
                }
            } else {
                resolve();
            }
        }).catch((error) => {
            console.log(error);
            // console.log("Error closing " + this._name + " Quadrant and its opened QuadrantPart: " + error.message);
        });
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = QuadrantComponent;
